import { useEffect, useState } from "react";
import { useKohde } from "./Kohdekonteksti";
import { api, ApiError, type Kohde, type Vaiheketju } from "./api";
import { t, esim, virheTeksti } from "./kieli";

/* ═══════════════════════════════════════════════════════════
   Laitteen luonti.

   `POST /api/laitteet` oli olemassa 26.8.2026 asti ilman että selain
   osasi kutsua sitä: moduulin sai päälle, mutta ensimmäisen laitteen
   sai kantaan vain SQL:llä. **Ominaisuus jonka voi ottaa käyttöön vain
   tietokantayhteydellä ei ole otettavissa käyttöön.**

   Kaksi asiaa jotka näkyvät lomakkeessa:

     · Sarjanumero on vapaaehtoinen eikä siitä huomauteta. Se selviää
       usein vasta vastaanotossa, ja tyhjä kenttä ei saa estää laitteen
       kirjaamista työmaalle.
     · Vaiheketjut kiinnitetään heti luonnissa. Ilman ketjua laitteella
       ei ole vaiheita, ja se näyttäisi näkymässä valmiilta — nolla
       vaihetta joista nolla on kuitattu.
   ═══════════════════════════════════════════════════════════ */

export default function LaiteLuonti({ valmis }: { valmis: () => void }) {
  const [auki, setAuki] = useState(false);
  const [kohteet, setKohteet] = useState<Kohde[]>([]);
  const [ketjut, setKetjut] = useState<Vaiheketju[]>([]);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [tallentaa, setTallentaa] = useState(false);

  const [kohdeId, setKohdeId] = useState<number | "">("");
  // T589 (arkkitehtuuri vaihe 1, kohdekoukku; Toni 16.10.2026 klo 03.45): oletus yläpalkin kohteesta (useKohde), vaihdettavissa.
  const { valittu: ylaKohde } = useKohde();
  useEffect(() => { if (typeof ylaKohde === "number" && kohdeId === "") setKohdeId(ylaKohde); }, [ylaKohde]);
  const [valmistaja, setValmistaja] = useState("");
  const [malli, setMalli] = useState("");
  const [sarjanumero, setSarjanumero] = useState("");
  const [tunnus, setTunnus] = useState("");
  const [sijainti, setSijainti] = useState("");
  const [asennus, setAsennus] = useState<number | "">("");
  const [kaytto, setKaytto] = useState<number | "">("");

  useEffect(() => {
    if (!auki) return;
    Promise.all([api.kohteet(), api.vaiheketjut()])
      .then(([k, v]) => {
        setKohteet(k);
        setKetjut(v);
        // Yksi vaihtoehto per laji: esivalitaan, koska valinta jota ei
        // ole ei ole valinta.
        const a = v.filter((x) => x.laji === "asennus");
        const b = v.filter((x) => x.laji === "kayttoonotto");
        if (a.length === 1) setAsennus(a[0]!.id);
        if (b.length === 1) setKaytto(b[0]!.id);
      })
      .catch((e) => setVirhe(e instanceof ApiError ? e.message : String(e)));
  }, [auki]);

  async function tallenna(e: React.FormEvent) {
    e.preventDefault();
    if (kohdeId === "" || !valmistaja.trim() || !malli.trim()) return;
    setTallentaa(true);
    setVirhe(null);
    try {
      const { id } = await api.luoLaite({
        kohde_id: Number(kohdeId),
        valmistaja: valmistaja.trim(),
        malli: malli.trim(),
        sarjanumero: sarjanumero.trim() || null,
        tunnus: tunnus.trim() || null,
        sijainti: sijainti.trim() || null,
      });
      if (asennus !== "") await api.kiinnitaVaiheketju(id, "asennus", Number(asennus));
      if (kaytto !== "") await api.kiinnitaVaiheketju(id, "kayttoonotto", Number(kaytto));
      setValmistaja(""); setMalli(""); setSarjanumero(""); setTunnus(""); setSijainti("");
      setAuki(false);
      valmis();
    } catch (err) {
      setVirhe(err instanceof ApiError ? virheTeksti(err.virhe, err.message) : t("ltar.tallennus_virhe"));
    } finally {
      setTallentaa(false);
    }
  }

  if (!auki) {
    return (
      <button className="nappi nappi--paa" onClick={() => setAuki(true)} data-avaa-lomake="laiteluonti">
        {t("muu.laite.lisaa")}
      </button>
    );
  }

  const ehdotus = ketjut.some((k) => k.lahde === "ehdotus");
  return (
    <form className="panel laiteluonti" onSubmit={(e) => void tallenna(e)}>
      <div className="panel__head">
        <h2>{t("muu.laite.uusi")}</h2>
        <button type="button" className="nappi nappi--kevyt" onClick={() => setAuki(false)}>
          {t("hl.peruuta")}
        </button>
      </div>

      {virhe && <p className="virhe">{virhe}</p>}

      <label>
        {t("muu.laite.tyomaa")}
        <select value={kohdeId} onChange={(e) => setKohdeId(e.target.value === "" ? "" : Number(e.target.value))} required data-kohdekoukku="laiteluonti">
          <option value="">{t("hl.valitse_viivat")}</option>
          {kohteet.map((k) => (
            <option key={k.id} value={k.id}>{k.tunnus} · {k.nimi}</option>
          ))}
        </select>
      </label>

      <label>
        {t("muu.laite.valmistaja")}
        <input value={valmistaja} onChange={(e) => setValmistaja(e.target.value)}
               maxLength={120} required placeholder={esim("Kylmälaite Oy")} />
      </label>

      <label>
        {t("muu.laite.malli")}
        <input value={malli} onChange={(e) => setMalli(e.target.value)}
               maxLength={120} required placeholder={esim("KL-800")} />
      </label>

      <label>
        {t("muu.laite.sarjanumero")} <span className="dim">{t("muu.laite.vapaaehtoinen")}</span>
        <input value={sarjanumero} onChange={(e) => setSarjanumero(e.target.value)}
               maxLength={120} placeholder={t("muu.laite.sarjanumero_ph")} />
      </label>

      <label>
        {t("muu.laite.tunnus")} <span className="dim">{t("muu.laite.tunnus_selite")}</span>
        <input value={tunnus} onChange={(e) => setTunnus(e.target.value)}
               maxLength={60} placeholder="CDU-3" />
      </label>

      <label>
        {t("muu.laite.sijainti")}
        <input value={sijainti} onChange={(e) => setSijainti(e.target.value)}
               maxLength={200} placeholder={t("muu.laite.sijainti_ph")} />
      </label>

      <label>
        {t("muu.laite.asennusketju")}
        <select value={asennus} onChange={(e) => setAsennus(e.target.value === "" ? "" : Number(e.target.value))}>
          <option value="">{t("muu.laite.ei_ketjua")}</option>
          {ketjut.filter((k) => k.laji === "asennus").map((k) => (
            <option key={k.id} value={k.id}>{k.nimi} ({t("muu.laite.vaihetta", { n: k.vaiheet.length })})</option>
          ))}
        </select>
      </label>

      <label>
        {t("muu.laite.kayttoketju")}
        <select value={kaytto} onChange={(e) => setKaytto(e.target.value === "" ? "" : Number(e.target.value))}>
          <option value="">{t("muu.laite.ei_ketjua")}</option>
          {ketjut.filter((k) => k.laji === "kayttoonotto").map((k) => (
            <option key={k.id} value={k.id}>{k.nimi} ({t("muu.laite.vaihetta", { n: k.vaiheet.length })})</option>
          ))}
        </select>
      </label>

      {/* Ehdotusketju sanotaan ääneen: se ei ole kenenkään vahvistama,
          ja siihen ehtii tottua jos sitä ei merkitä. */}
      {ehdotus && (
        <p className="hint">
          {t("muu.laite.ehdotus1")} <strong>{t("muu.laite.ehdotus2")}</strong> — {t("muu.laite.ehdotus3")}
        </p>
      )}

      <button type="submit" className="nappi nappi--paa" disabled={tallentaa}>
        {tallentaa ? t("pu.tallennetaan") : t("muu.laite.luo")}
      </button>
    </form>
  );
}
